import toast from 'react-hot-toast';

// Notification types and their configurations
export const NOTIFICATION_TYPES = {
  TRIP_CREATED: 'trip_created',
  TRIP_JOINED: 'trip_joined',
  TRIP_CONFIRMED: 'trip_confirmed',
  TRIP_CANCELLED: 'trip_cancelled',
  PAYMENT_AUTHORIZED: 'payment_authorized',
  PAYMENT_CAPTURED: 'payment_captured',
  PAYMENT_REFUNDED: 'payment_refunded',
  FALLBACK_PENDING: 'fallback_pending',
  FALLBACK_ACCEPTED: 'fallback_accepted',
  FALLBACK_DECLINED: 'fallback_declined',
  SEAT_COUNT_UPDATED: 'seat_count_updated',
  TRIP_LOCKED: 'trip_locked',
  TRIP_UNLOCKED: 'trip_unlocked',
  MINIMUM_MET: 'minimum_met',
  MINIMUM_NOT_MET: 'minimum_not_met'
};

// Notification configurations
const NOTIFICATION_CONFIGS = {
  [NOTIFICATION_TYPES.TRIP_CREATED]: {
    title: 'Trip Created',
    message: 'Your trip has been created successfully!',
    type: 'success',
    icon: '🚗',
    duration: 4000
  },
  [NOTIFICATION_TYPES.TRIP_JOINED]: {
    title: 'Trip Joined',
    message: 'You have successfully joined the trip!',
    type: 'success',
    icon: '✅',
    duration: 4000
  },
  [NOTIFICATION_TYPES.TRIP_CONFIRMED]: {
    title: 'Trip Confirmed',
    message: 'Your trip is confirmed and payment will be captured!',
    type: 'success',
    icon: '🎉',
    duration: 5000
  },
  [NOTIFICATION_TYPES.TRIP_CANCELLED]: {
    title: 'Trip Cancelled',
    message: 'The trip has been cancelled. Any charges will be refunded.',
    type: 'error',
    icon: '❌',
    duration: 5000
  },
  [NOTIFICATION_TYPES.PAYMENT_AUTHORIZED]: {
    title: 'Payment Authorized',
    message: 'Your card has been authorized. No charges until trip confirms.',
    type: 'success',
    icon: '💳',
    duration: 4000
  },
  [NOTIFICATION_TYPES.PAYMENT_CAPTURED]: {
    title: 'Payment Captured',
    message: 'Payment has been captured successfully!',
    type: 'success',
    icon: '💰',
    duration: 4000
  },
  [NOTIFICATION_TYPES.PAYMENT_REFUNDED]: {
    title: 'Payment Refunded',
    message: 'Your payment has been refunded successfully.',
    type: 'success',
    icon: '💸',
    duration: 4000
  },
  [NOTIFICATION_TYPES.FALLBACK_PENDING]: {
    title: 'Trip at Risk',
    message: 'Minimum seats not met. You can accept higher pricing or cancel.',
    type: 'warning',
    icon: '⚠️',
    duration: 6000
  },
  [NOTIFICATION_TYPES.FALLBACK_ACCEPTED]: {
    title: 'Fallback Accepted',
    message: 'Trip will proceed with higher pricing.',
    type: 'success',
    icon: '✅',
    duration: 4000
  },
  [NOTIFICATION_TYPES.FALLBACK_DECLINED]: {
    title: 'Trip Cancelled',
    message: 'Trip cancelled. Any charges will be refunded.',
    type: 'error',
    icon: '❌',
    duration: 4000
  },
  [NOTIFICATION_TYPES.SEAT_COUNT_UPDATED]: {
    title: 'Seat Count Updated',
    message: 'The number of participants has changed.',
    type: 'info',
    icon: '👥',
    duration: 3000
  },
  [NOTIFICATION_TYPES.TRIP_LOCKED]: {
    title: 'Trip Locked',
    message: 'Trip is now locked for final preparations.',
    type: 'info',
    icon: '🔒',
    duration: 4000
  },
  [NOTIFICATION_TYPES.TRIP_UNLOCKED]: {
    title: 'Trip Unlocked',
    message: 'Trip is now open for modifications.',
    type: 'info',
    icon: '🔓',
    duration: 4000
  },
  [NOTIFICATION_TYPES.MINIMUM_MET]: {
    title: 'Minimum Met',
    message: 'Trip will be confirmed! Payment will be captured.',
    type: 'success',
    icon: '🎯',
    duration: 4000
  },
  [NOTIFICATION_TYPES.MINIMUM_NOT_MET]: {
    title: 'Minimum Not Met',
    message: 'Trip needs more participants to confirm.',
    type: 'warning',
    icon: '📊',
    duration: 4000
  }
};

// Show notification with custom configuration
export const showNotification = (type, customMessage = null, customDuration = null) => {
  try {
    const config = NOTIFICATION_CONFIGS[type];
    if (!config) {
      console.warn(`Unknown notification type: ${type}`);
      return;
    }

    const message = customMessage || config.message;
    const duration = customDuration || config.duration;

    const toastOptions = {
      duration,
      icon: config.icon,
      style: {
        background: getBackgroundColor(config.type),
        color: '#fff',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500'
      }
    };

    switch (config.type) {
      case 'success':
        toast.success(message, toastOptions);
        break;
      case 'error':
        toast.error(message, toastOptions);
        break;
      case 'warning':
        toast(message, {
          ...toastOptions,
          icon: config.icon,
          style: {
            ...toastOptions.style,
            background: '#F59E0B',
            color: '#fff'
          }
        });
        break;
      case 'info':
        toast(message, {
          ...toastOptions,
          icon: config.icon,
          style: {
            ...toastOptions.style,
            background: '#3B82F6',
            color: '#fff'
          }
        });
        break;
      default:
        toast(message, toastOptions);
    }
  } catch (error) {
    console.error('❌ Error showing notification:', error);
  }
};

// Get background color based on notification type
const getBackgroundColor = (type) => {
  switch (type) {
    case 'success':
      return '#10B981';
    case 'error':
      return '#EF4444';
    case 'warning':
      return '#F59E0B';
    case 'info':
      return '#3B82F6';
    default:
      return '#6B7280';
  }
};

// Trip-specific notifications
export const showTripNotification = {
  created: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.TRIP_CREATED,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} created successfully!`
    );
  },

  joined: (tripDetails, groupSize) => {
    showNotification(
      NOTIFICATION_TYPES.TRIP_JOINED,
      `Successfully joined trip for ${groupSize} ${groupSize === 1 ? 'person' : 'people'}!`
    );
  },

  confirmed: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.TRIP_CONFIRMED,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} is confirmed!`
    );
  },

  cancelled: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.TRIP_CANCELLED,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} has been cancelled.`
    );
  },

  seatCountUpdated: (oldCount, newCount, tripDetails) => {
    const change = newCount - oldCount;
    const changeText = change > 0 ? `+${change}` : change.toString();
    showNotification(
      NOTIFICATION_TYPES.SEAT_COUNT_UPDATED,
      `Seat count changed: ${oldCount} → ${newCount} (${changeText})`
    );
  },

  minimumMet: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.MINIMUM_MET,
      `Minimum met — trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} will confirm.`
    );
  },

  minimumNotMet: (currentSeats, minSeats, tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.MINIMUM_NOT_MET,
      `${currentSeats} of ${minSeats} riders — trip at risk (${tripDetails.pickup_location} → ${tripDetails.dropoff_location}).`
    );
  },

  fallbackPending: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.FALLBACK_PENDING,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} needs more participants. You can accept higher pricing or cancel.`
    );
  }
};

// Payment-specific notifications
export const showPaymentNotification = {
  authorized: (amount) => {
    showNotification(
      NOTIFICATION_TYPES.PAYMENT_AUTHORIZED,
      `Card authorized for $${amount.toFixed(2)}. No charges until trip confirms.`
    );
  },

  captured: (amount) => {
    showNotification(
      NOTIFICATION_TYPES.PAYMENT_CAPTURED,
      `Payment of $${amount.toFixed(2)} captured successfully!`
    );
  },

  refunded: (amount) => {
    showNotification(
      NOTIFICATION_TYPES.PAYMENT_REFUNDED,
      `Payment of $${amount.toFixed(2)} refunded successfully.`
    );
  }
};

// Fallback-specific notifications
export const showFallbackNotification = {
  accepted: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.FALLBACK_ACCEPTED,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} will proceed with higher pricing.`
    );
  },

  declined: (tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.FALLBACK_DECLINED,
      `Trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location} cancelled. Any charges will be refunded.`
    );
  }
};

// Real-time update notifications
export const showRealTimeNotification = {
  participantJoined: (participantName, tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.SEAT_COUNT_UPDATED,
      `${participantName} joined the trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location}!`
    );
  },

  participantLeft: (participantName, tripDetails) => {
    showNotification(
      NOTIFICATION_TYPES.SEAT_COUNT_UPDATED,
      `${participantName} left the trip from ${tripDetails.pickup_location} to ${tripDetails.dropoff_location}.`
    );
  },

  tripStatusChanged: (oldStatus, newStatus, tripDetails) => {
    const statusMessages = {
      'open': 'Trip is now open for joining',
      'confirmed': 'Trip is now confirmed!',
      'fallback_pending': 'Trip is awaiting confirmation',
      'locked': 'Trip is locked for final preparations',
      'cancelled': 'Trip has been cancelled'
    };

    const message = statusMessages[newStatus] || `Trip status changed to ${newStatus}`;
    showNotification(
      NOTIFICATION_TYPES.SEAT_COUNT_UPDATED,
      message
    );
  }
};

// Utility function to show custom notifications
export const showCustomNotification = (message, type = 'info', duration = 4000) => {
  const toastOptions = {
    duration,
    style: {
      background: getBackgroundColor(type),
      color: '#fff',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  switch (type) {
    case 'success':
      toast.success(message, toastOptions);
      break;
    case 'error':
      toast.error(message, toastOptions);
      break;
    case 'warning':
      toast(message, {
        ...toastOptions,
        style: {
          ...toastOptions.style,
          background: '#F59E0B'
        }
      });
      break;
    case 'info':
      toast(message, {
        ...toastOptions,
        style: {
          ...toastOptions.style,
          background: '#3B82F6'
        }
      });
      break;
    default:
      toast(message, toastOptions);
  }
};

const notificationUtils = {
  showNotification,
  showTripNotification,
  showPaymentNotification,
  showFallbackNotification,
  showRealTimeNotification,
  showCustomNotification,
  NOTIFICATION_TYPES
};

export default notificationUtils; 